import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../../utils/constants';
import AdminNav from '../../components/admin/AdminNav';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     bg: 'bg-white/10',           text: 'text-white/70',    dot: 'bg-white/40' },
  active:    { label: 'Active',    bg: 'bg-[#0DB14B]/20',       text: 'text-[#0DB14B]',   dot: 'bg-[#0DB14B]' },
  completed: { label: 'Completed', bg: 'bg-[#235EAC]/20',       text: 'text-[#235EAC]',   dot: 'bg-[#235EAC]' },
  archived:  { label: 'Archived',  bg: 'bg-[#939598]/20',       text: 'text-[#939598]',   dot: 'bg-[#939598]' },
};

const SCOPE_OPTIONS = [
  { value: 'company', label: 'Company-Wide' },
  { value: 'region',  label: 'Regional' },
  { value: 'branch',  label: 'Branch-Specific' },
];

const PBFS_REGIONS = [
  'Southeast',
  'Southwest',
  'Midwest',
  'Mid-Atlantic',
  'Northeast',
  'West',
  'South',
];

const EMPTY_FORM = {
  name: '',
  description: '',
  start_date: '',
  end_date: '',
  scope: 'company',
  scope_filter: [],
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const CampaignManagement = () => {
  const [navMode, setNavMode] = useState(() =>
    localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded')
  );
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmAction, setConfirmAction] = useState(null);

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4';
      case 'collapsed': return 'ml-20';
      default: return 'ml-64';
    }
  };

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/api/admin/campaigns`, { headers: getHeaders() });
      if (!res.ok) throw new Error(`Failed to load campaigns (${res.status})`);
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const openCreate = () => {
    setEditingCampaign(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);
    setForm({
      name: campaign.name || '',
      description: campaign.description || '',
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      scope: campaign.scope || 'company',
      scope_filter: campaign.scope_filter || [],
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingCampaign(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date || !form.end_date) return;
    if (form.start_date >= form.end_date) {
      setError('Start date must be before end date');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const url = editingCampaign
        ? `${BACKEND_URL}/api/admin/campaigns/${editingCampaign.id}`
        : `${BACKEND_URL}/api/admin/campaigns`;
      const method = editingCampaign ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          scope: form.scope,
          scope_filter: form.scope_filter,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Request failed (${res.status})`);
      }

      closeForm();
      await fetchCampaigns();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (campaign, newStatus) => {
    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Failed to update status (${res.status})`);
      }
      setConfirmAction(null);
      await fetchCampaigns();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const archiveCampaign = async (campaign) => {
    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaign.id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to archive (${res.status})`);
      setConfirmAction(null);
      await fetchCampaigns();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusActions = (campaign) => {
    const actions = [];
    switch (campaign.status) {
      case 'draft':
        actions.push({ label: 'Activate', status: 'active', color: 'bg-[#0DB14B] hover:bg-[#0a9e40]' });
        actions.push({ label: 'Archive', action: 'archive', color: 'bg-[#939598]/30 hover:bg-[#939598]/50 text-[#939598]' });
        break;
      case 'active':
        actions.push({ label: 'Complete', status: 'completed', color: 'bg-[#235EAC] hover:bg-[#1c4e8f]' });
        break;
      case 'completed':
        actions.push({ label: 'Reactivate', status: 'active', color: 'bg-[#0DB14B] hover:bg-[#0a9e40]' });
        actions.push({ label: 'Archive', action: 'archive', color: 'bg-[#939598]/30 hover:bg-[#939598]/50 text-[#939598]' });
        break;
      case 'archived':
        actions.push({ label: 'Restore to Draft', status: 'draft', color: 'bg-white/10 hover:bg-white/20' });
        break;
      default:
        break;
    }
    return actions;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filtered = statusFilter === 'all'
    ? campaigns
    : campaigns.filter(c => c.status === statusFilter);

  const counts = campaigns.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  if (loading && campaigns.length === 0) {
    return (
      <div className="min-h-screen bg-[#00314F]">
        <AdminNav onModeChange={setNavMode} />
        <div className={`${getNavOffset()} transition-all duration-300 px-4 py-4 pt-20`}>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-white/20 border-t-[#0DB14B] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white text-lg">Loading campaigns...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#00314F]">
      <AdminNav onModeChange={setNavMode} />
      <div className={`${getNavOffset()} transition-all duration-300 px-4 py-4 pt-20`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <span className="text-3xl">🏆</span>
              Campaigns
            </h1>
            <p className="text-white/60 mt-2">
              Create and manage leaderboard campaigns for competitions and promotions
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0DB14B] hover:bg-[#0a9e40] text-white font-semibold rounded-lg transition-colors shadow-lg shadow-[#0DB14B]/20"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Campaign
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center justify-between">
            <p className="text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 ml-4">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All', count: campaigns.length },
            { key: 'active', label: 'Active', count: counts.active || 0 },
            { key: 'draft', label: 'Drafts', count: counts.draft || 0 },
            { key: 'completed', label: 'Completed', count: counts.completed || 0 },
            { key: 'archived', label: 'Archived', count: counts.archived || 0 },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === tab.key
                  ? 'bg-white/15 text-white'
                  : 'text-white/50 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                statusFilter === tab.key ? 'bg-white/20' : 'bg-white/10'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Campaign List */}
        {filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center">
            <div className="text-5xl mb-4">🏆</div>
            <h3 className="text-xl font-bold text-white mb-2">
              {statusFilter === 'all' ? 'No campaigns yet' : `No ${statusFilter} campaigns`}
            </h3>
            <p className="text-white/50 mb-6">
              {statusFilter === 'all'
                ? 'Create your first campaign to run a leaderboard competition.'
                : 'Try a different filter or create a new campaign.'}
            </p>
            {statusFilter === 'all' && (
              <button
                onClick={openCreate}
                className="px-5 py-2.5 bg-[#0DB14B] hover:bg-[#0a9e40] text-white font-semibold rounded-lg transition-colors"
              >
                Create Campaign
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(campaign => (
              <div
                key={campaign.id}
                className="bg-white/5 hover:bg-white/[0.07] border border-white/10 rounded-xl p-5 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Left: Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white truncate">{campaign.name}</h3>
                      <StatusBadge status={campaign.status} />
                    </div>
                    {campaign.description && (
                      <p className="text-white/50 text-sm mb-3 line-clamp-2">{campaign.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                      <span className="text-white/40 flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(campaign.start_date)} — {formatDate(campaign.end_date)}
                      </span>
                      <span className="text-white/40 flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                        </svg>
                        {SCOPE_OPTIONS.find(s => s.value === campaign.scope)?.label || campaign.scope}
                        {campaign.scope_filter?.length > 0 && (
                          <span className="text-white/30">
                            ({campaign.scope_filter.join(', ')})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openEdit(campaign)}
                      className="px-3.5 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Edit
                    </button>
                    {getStatusActions(campaign).map(action => (
                      <button
                        key={action.label}
                        disabled={saving}
                        onClick={() => setConfirmAction({ campaign, ...action })}
                        className={`px-3.5 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${action.color}`}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirm Dialog */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#00314F] border border-white/20 rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-lg font-bold text-white mb-3">
                {confirmAction.action === 'archive' ? 'Archive' : confirmAction.label} Campaign?
              </h3>
              <p className="text-white/60 mb-6">
                {confirmAction.action === 'archive'
                  ? `"${confirmAction.campaign.name}" will be archived and hidden from the leaderboard.`
                  : `"${confirmAction.campaign.name}" will be set to ${confirmAction.status}.`}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={saving}
                  className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    confirmAction.action === 'archive'
                      ? archiveCampaign(confirmAction.campaign)
                      : updateStatus(confirmAction.campaign, confirmAction.status)
                  }
                  disabled={saving}
                  className={`px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${confirmAction.color}`}
                >
                  {saving ? 'Saving...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create / Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#00314F] border border-white/20 rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">
                    {editingCampaign ? 'Edit Campaign' : 'New Campaign'}
                  </h2>
                  <button
                    onClick={closeForm}
                    className="text-white/40 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1.5">Campaign Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Spring 2026 Launch"
                      className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#0DB14B] transition-colors"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1.5">Description</label>
                    <textarea
                      value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Optional description for this campaign"
                      rows={3}
                      className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#0DB14B] transition-colors resize-none"
                    />
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1.5">Start Date *</label>
                      <input
                        type="date"
                        required
                        value={form.start_date}
                        onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                        className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#0DB14B] transition-colors"
                        style={{ colorScheme: 'dark' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1.5">End Date *</label>
                      <input
                        type="date"
                        required
                        value={form.end_date}
                        onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                        className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#0DB14B] transition-colors"
                        style={{ colorScheme: 'dark' }}
                      />
                    </div>
                  </div>

                  {/* Scope */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1.5">Scope</label>
                    <select
                      value={form.scope}
                      onChange={e => setForm(f => ({ ...f, scope: e.target.value, scope_filter: [] }))}
                      className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#0DB14B] transition-colors"
                      style={{ colorScheme: 'dark' }}
                    >
                      {SCOPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value} className="bg-[#00314F] text-white">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Region Selector — visible when scope is region or branch */}
                  {(form.scope === 'region' || form.scope === 'branch') && (
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1.5">
                        {form.scope === 'region' ? 'Participating Regions' : 'Branches in Regions'}
                      </label>
                      <p className="text-white/30 text-xs mb-2">
                        Select which regions are included in this campaign
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {PBFS_REGIONS.map(region => {
                          const selected = form.scope_filter.includes(region);
                          return (
                            <button
                              key={region}
                              type="button"
                              onClick={() => {
                                setForm(f => ({
                                  ...f,
                                  scope_filter: selected
                                    ? f.scope_filter.filter(r => r !== region)
                                    : [...f.scope_filter, region],
                                }));
                              }}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                                selected
                                  ? 'bg-[#0DB14B]/20 border-[#0DB14B]/50 text-[#0DB14B]'
                                  : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/70'
                              }`}
                            >
                              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                                selected ? 'border-[#0DB14B] bg-[#0DB14B]' : 'border-white/30'
                              }`}>
                                {selected && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              {region}
                            </button>
                          );
                        })}
                      </div>
                      {form.scope_filter.length > 0 && (
                        <p className="text-white/40 text-xs mt-2">
                          {form.scope_filter.length} of {PBFS_REGIONS.length} regions selected
                        </p>
                      )}
                    </div>
                  )}

                  {editingCampaign && (
                    <div className="pt-2 border-t border-white/10">
                      <p className="text-white/30 text-xs">
                        Created {formatDate(editingCampaign.created_at?.slice(0, 10))}
                        {editingCampaign.updated_at && ` · Updated ${formatDate(editingCampaign.updated_at.slice(0, 10))}`}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeForm}
                      disabled={saving}
                      className="px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving || !form.name.trim() || !form.start_date || !form.end_date}
                      className="px-5 py-2.5 bg-[#0DB14B] hover:bg-[#0a9e40] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : editingCampaign ? 'Save Changes' : 'Create Campaign'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignManagement;
